import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardLayout";
import { BackButton } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, fmtSASTDateTime, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, CheckCircle2, XCircle } from "lucide-react";

export default function AdminKYC() {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState({});

  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/admin/kyc/pending").then(({ data }) => setItems(data.pending || []));
  useEffect(() => { load(); }, []);

  const review = async (id, status) => {
    setBusyId(id);
    try {
      await api.post(`/admin/kyc/${id}/review`, { status, notes: notes[id] || "" });
      // Optimistically remove from the pending list so the buttons can't be
      // clicked twice for the same merchant.
      setItems((curr) => curr.filter((m) => m.id !== id));
      toast.success(`Merchant ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <DashboardShell role="admin">
      <BackButton to="/admin" />
      <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">KYC queue</p>
      <h1 className="font-display mt-2 text-3xl font-bold text-[#0A2540]">Pending KYC submissions ({items.length})</h1>

      <div className="mt-6 space-y-5">
        {items.length === 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-10 text-center text-[#666666]">
            No pending submissions.
          </div>
        )}
        {items.map((m) => (
          <div key={m.id} data-testid={`kyc-card-${m.id}`} className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="font-display text-lg font-semibold text-[#0A2540]">{m.full_name || m.contact_name}</p>
                <p className="text-xs text-[#666666] mt-1 uppercase tracking-wider">{m.account_type} · submitted {fmtSASTDateTime(m.kyc_submitted_at)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm"><strong className="text-[#0A2540]">ID:</strong> {m.id_number}</p>
                <p className="text-sm"><strong className="text-[#0A2540]">Cell:</strong> {m.cellphone}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
              <p><span className="text-[#666666]">Address:</span> {m.residential_address}</p>
              {m.business_name && <p><span className="text-[#666666]">Business:</span> {m.business_name}</p>}
              {m.cipc_number && <p><span className="text-[#666666]">CIPC:</span> {m.cipc_number}</p>}
              {m.director_id_number && <p><span className="text-[#666666]">Director ID:</span> {m.director_id_number}</p>}
            </div>

            {m.ocr_extracted?.length > 0 && (
              <details className="mt-4 bg-[#F8F9FA] rounded-lg p-3 border border-[#E5E7EB]">
                <summary className="text-xs uppercase tracking-wider font-semibold text-[#00C4B4] cursor-pointer">OCR extraction</summary>
                <pre className="mt-3 text-[11px] text-[#333333] overflow-x-auto">{JSON.stringify(m.ocr_extracted, null, 2)}</pre>
              </details>
            )}

            {m.documents?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {m.documents.map((d) => (
                  <a key={d.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.file_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#F8F9FA] border border-[#E5E7EB] rounded-full text-[#0A2540] hover:border-[#00C4B4]">
                    <FileText size={12} /> {d.file_id.substring(0, 8)}…
                  </a>
                ))}
              </div>
            )}

            <Textarea
              data-testid={`kyc-notes-${m.id}`}
              placeholder="Admin notes (optional)"
              rows={2}
              value={notes[m.id] || ""}
              onChange={(e) => setNotes({ ...notes, [m.id]: e.target.value })}
              className="mt-4"
            />
            <div className="flex gap-2 mt-3">
              <Button data-testid={`kyc-approve-${m.id}`} onClick={() => review(m.id, "approved")} disabled={busyId === m.id} className="bg-[#00C853] hover:bg-[#00B348] text-white disabled:opacity-50"><CheckCircle2 size={16} className="mr-2" />{busyId === m.id ? "Working…" : "Approve"}</Button>
              <Button data-testid={`kyc-reject-${m.id}`} onClick={() => review(m.id, "rejected")} disabled={busyId === m.id} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"><XCircle size={16} className="mr-2" />Reject</Button>
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}
